import React, { useState, useEffect, useCallback, useRef } from 'react';

const TableEditor = ({ 
  data, 
  onChange, 
  isDarkMode, 
  highlights, 
  onHighlight, 
  textractMetadata,
  onCellSelect  
}) => {
  const [currentCell, setCurrentCell] = useState({ row: null, col: null });
  const [editingCell, setEditingCell] = useState({ row: null, col: null });
  const [isDragging, setIsDragging] = useState(false);
  const [selectionRange, setSelectionRange] = useState({
    start: { row: null, col: null },
    end: { row: null, col: null }
  });
  
  // State for multi-selection with Ctrl key
  const [multiSelectRanges, setMultiSelectRanges] = useState([]);
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);
  
  // Add state for undo history
  const [undoHistory, setUndoHistory] = useState([]);
  const MAX_HISTORY = 10; // Max number of changes to track
  
  // Create refs for cell elements to scroll them into view
  const gridRef = useRef(null);
  const cellRefs = useRef({});
  const scrollContainerRef = useRef(null);
  const tableWrapperRef = useRef(null);
  
  // For clipboard operations with multiple cells
  const clipboardRef = useRef(null);

  // Track if we've already applied highlights to avoid multiple applications
  const highlightsAppliedRef = useRef(false);

  useEffect(() => {
    // Skip if we don't have necessary data
    if (!data || !data.length || !textractMetadata?.cells || !onHighlight) return;
    
    // NEW: Check if any highlights already exist (meaning this table has been viewed before)
    const hasExistingHighlights = Object.keys(highlights).length > 0;
    
    // Only apply automatic highlights if no highlights exist yet
    if (hasExistingHighlights) {
      // console.log('Table already has highlights, skipping automatic highlighting');
      return;
    }
    
    // console.log('First-time table view: applying default confidence threshold');
    
    // Get the confidence threshold
    const threshold = textractMetadata?.confidenceThreshold || 80;
    
    // Process each cell for low confidence
    data.forEach((row, rowIndex) => {
      if (!row) return;
      
      row.forEach((_, colIndex) => {
        // Skip if this cell is out of bounds in the metadata
        if (!textractMetadata.cells[rowIndex] || !textractMetadata.cells[rowIndex][colIndex]) return;
        
        // Get cell confidence
        const cell = textractMetadata.cells[rowIndex][colIndex];
        if (!cell || typeof cell.confidence !== 'number') return;
        
        const key = `${rowIndex}-${colIndex}`;
        
        // Apply highlight if low confidence
        if (cell.confidence < threshold) {
          onHighlight(rowIndex, colIndex, '#f44336');
        }
      });
    });
  }, [data, textractMetadata, highlights, onHighlight]);

  const isInRange = useCallback((row, col) => {
    const { start, end } = selectionRange;
    if (start.row === null || end.row === null || start.col === null || end.col === null) return false;
    
    const minRow = Math.min(start.row, end.row);
    const maxRow = Math.max(start.row, end.row);
    const minCol = Math.min(start.col, end.col);
    const maxCol = Math.max(start.col, end.col);
    
    return row >= minRow && row <= maxRow && col >= minCol && col <= maxCol;
  }, [selectionRange]);

  // Helper to check if cell is in any of the multi-selected ranges
  const isInMultiSelectRange = useCallback((row, col) => {
    return multiSelectRanges.some(range => {
      const { start, end } = range;
      const minRow = Math.min(start.row, end.row);
      const maxRow = Math.max(start.row, end.row);
      const minCol = Math.min(start.col, end.col);
      const maxCol = Math.max(start.col, end.col);
      
      return row >= minRow && row <= maxRow && col >= minCol && col <= maxCol;
    });
  }, [multiSelectRanges]);

  // Helper to add current state to undo history
  const addToHistory = useCallback((oldData) => {
    setUndoHistory(prev => {
      const newHistory = [...prev, oldData];
      // Keep only the last MAX_HISTORY items
      if (newHistory.length > MAX_HISTORY) {
        return newHistory.slice(newHistory.length - MAX_HISTORY);
      }
      return newHistory;
    });
  }, []);

  // Function to handle undo
  const handleUndo = useCallback(() => {
    if (undoHistory.length === 0) return;
    
    // Get the last state from history
    const lastData = undoHistory[undoHistory.length - 1];
    
    // Apply it
    onChange(lastData);
    
    // Remove it from history
    setUndoHistory(prev => prev.slice(0, prev.length - 1));
  }, [undoHistory, onChange]);

  const selectEntireRow = (rowIndex, e) => {
    if (!data[0]) return; // Guard against empty data
    
    const start = { row: rowIndex, col: 0 };
    const end = { row: rowIndex, col: data[0].length - 1 };
    
    // Check for Ctrl key from event or state
    const useCtrl = (e && e.ctrlKey) || isCtrlPressed;
    
    if (useCtrl) {
      // Add to multi-selection
      setMultiSelectRanges(prev => [...prev, { start, end }]);
    } else {
      // Regular selection
      setSelectionRange({ start, end });
      // Set current cell to the FIRST cell of the row (improvement #3)
      setCurrentCell({ row: rowIndex, col: 0 });
    }
    
    setEditingCell({ row: null, col: null });
    
    // Calculate selected cells for this row
    const selectedCells = [];
    for (let c = 0; c <= data[0].length - 1; c++) {
      selectedCells.push({ row: rowIndex, col: c });
    }
    
    // Call onCellSelect with all necessary information
    onCellSelect?.(rowIndex, 0, { start, end }, selectedCells);
  };

  const selectEntireColumn = (colIndex, e) => {
    if (!data.length) return;
    const lastRowIndex = data.length - 1;
    
    const start = { row: 0, col: colIndex };
    const end = { row: lastRowIndex, col: colIndex };
    
    // Check for Ctrl key from event or state
    const useCtrl = (e && e.ctrlKey) || isCtrlPressed;
    
    if (useCtrl) {
      // Add to multi-selection
      setMultiSelectRanges(prev => [...prev, { start, end }]);
    } else {
      // Regular selection
      setSelectionRange({ start, end });
      // Set current cell to the FIRST cell of the column (improvement #3)
      setCurrentCell({ row: 0, col: colIndex });
    }
    
    setEditingCell({ row: null, col: null });
    
    // Calculate selected cells for this column
    const selectedCells = [];
    for (let r = 0; r <= lastRowIndex; r++) {
      selectedCells.push({ row: r, col: colIndex });
    }
    
    // Call onCellSelect with all necessary information
    onCellSelect?.(0, colIndex, { start, end }, selectedCells);
  };

  const getSelectedCells = useCallback(() => {
    const cells = [];
    
    // Add cells from the main selection range
    const { start, end } = selectionRange;
    if (start.row !== null && end.row !== null) {
      const minRow = Math.min(start.row, end.row);
      const maxRow = Math.max(start.row, end.row);
      const minCol = Math.min(start.col, end.col);
      const maxCol = Math.max(start.col, end.col);

      for (let r = minRow; r <= maxRow; r++) {
        for (let c = minCol; c <= maxCol; c++) {
          cells.push({ row: r, col: c });
        }
      }
    }
    
    // Add cells from multi-select ranges
    multiSelectRanges.forEach(range => {
      const { start, end } = range;
      const minRow = Math.min(start.row, end.row);
      const maxRow = Math.max(start.row, end.row);
      const minCol = Math.min(start.col, end.col);
      const maxCol = Math.max(start.col, end.col);

      for (let r = minRow; r <= maxRow; r++) {
        for (let c = minCol; c <= maxCol; c++) {
          // Check if this cell is already in the array to avoid duplicates
          if (!cells.some(cell => cell.row === r && cell.col === c)) {
            cells.push({ row: r, col: c });
          }
        }
      }
    });
    
    return cells;
  }, [selectionRange, multiSelectRanges]);

  // Function to get selected range dimensions
  const getSelectionDimensions = useCallback(() => {
    const { start, end } = selectionRange;
    if (start.row === null || end.row === null) {
      return { rows: 0, cols: 0, minRow: 0, minCol: 0 };
    }

    const minRow = Math.min(start.row, end.row);
    const maxRow = Math.max(start.row, end.row);
    const minCol = Math.min(start.col, end.col);
    const maxCol = Math.max(start.col, end.col);

    return {
      rows: maxRow - minRow + 1,
      cols: maxCol - minCol + 1,
      minRow,
      minCol,
      maxRow,
      maxCol
    };
  }, [selectionRange]);

  // Copy selected cells to clipboard
  const copySelectedCells = useCallback(() => {
    // Don't allow copy on multi-select (improvement #4)
    if (multiSelectRanges.length > 0) {
      console.log('Copy not available for multi-selection');
      return;
    }
    
    const selectedCells = getSelectedCells();
    if (selectedCells.length === 0) return;

    const dimensions = getSelectionDimensions();
    const { rows, cols, minRow, minCol } = dimensions;
    
    // Create a 2D array for our clipboard data
    const clipboardData = Array(rows).fill().map(() => Array(cols).fill(''));
    
    // Fill with the selected cell values
    selectedCells.forEach(({ row, col }) => {
      const relativeRow = row - minRow;
      const relativeCol = col - minCol;
      clipboardData[relativeRow][relativeCol] = data[row][col] || '';
    });
    
    // Save to our ref for paste operations
    clipboardRef.current = {
      data: clipboardData,
      rows,
      cols
    };
    
    // Also copy to system clipboard as tab-separated text
    const tsv = clipboardData.map(row => row.join('\t')).join('\n');
    navigator.clipboard.writeText(tsv);
  }, [getSelectedCells, getSelectionDimensions, data, multiSelectRanges]);

  // Cut selected cells
  const cutSelectedCells = useCallback(() => {
    // Don't allow cut on multi-select (improvement #4)
    if (multiSelectRanges.length > 0) {
      console.log('Cut not available for multi-selection');
      return;
    }
    
    const selectedCells = getSelectedCells();
    if (selectedCells.length === 0) return;
    
    // First copy the selected cells
    copySelectedCells();
    
    // Then clear them
    // Save current state for undo
    addToHistory([...data]);
    
    const newData = [...data];
    selectedCells.forEach(({ row, col }) => {
      if (row < newData.length && col < newData[0].length) {
        newData[row] = [...newData[row]];
        newData[row][col] = '';
      }
    });
    
    onChange(newData);
  }, [getSelectedCells, copySelectedCells, data, addToHistory, onChange, multiSelectRanges]);

  // Paste from clipboard
  const pasteToCurrentCell = useCallback(() => {
    if (!clipboardRef.current || currentCell.row === null || currentCell.col === null) {
      // Try to paste from system clipboard as fallback
      navigator.clipboard.readText().then(text => {
        if (!text.trim()) return;
        
        // Simple paste to current cell
        const newData = [...data];
        newData[currentCell.row] = [...newData[currentCell.row]];
        newData[currentCell.row][currentCell.col] = text;
        
        // Save current state for undo
        addToHistory([...data]);
        onChange(newData);
      });
      return;
    }

    // Get clipboard data
    const { data: clipboardData, rows, cols } = clipboardRef.current;
    
    // Save current state for undo
    addToHistory([...data]);
    
    // Create new data
    const newData = [...data];
    
    // Paste starting at current cell
    const startRow = currentCell.row;
    const startCol = currentCell.col;
    
    // Iterate over the clipboard data and paste it
    for (let r = 0; r < rows; r++) {
      const targetRow = startRow + r;
      if (targetRow >= newData.length) continue;
      
      newData[targetRow] = [...newData[targetRow]];
      
      for (let c = 0; c < cols; c++) {
        const targetCol = startCol + c;
        if (targetCol >= newData[targetRow].length) continue;
        
        newData[targetRow][targetCol] = clipboardData[r][c];
      }
    }
    
    onChange(newData);
  }, [currentCell, data, addToHistory, onChange]);

  const prevSelectionRef = useRef({ row: null, col: null, range: null });

  // Monitor ctrl key state for multi-select functionality
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Control') {
        setIsCtrlPressed(true);
      }
    };
    
    const handleKeyUp = (e) => {
      if (e.key === 'Control') {
        setIsCtrlPressed(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Find the first scrollable parent container on component mount
  useEffect(() => {
    if (gridRef.current) {
      let container = gridRef.current;
      while (container && 
             (getComputedStyle(container).overflow === 'visible' || 
              getComputedStyle(container).overflow === 'hidden')) {
        container = container.parentElement;
        if (!container) break;
      }
      scrollContainerRef.current = container;
    }
  }, []);

  // Function to scroll cell into view
  const scrollCellIntoView = useCallback((row, col) => {
    const cellKey = `${row}-${col}`;
    const cellElement = cellRefs.current[cellKey];
    const scrollContainer = scrollContainerRef.current;
    
    if (cellElement && scrollContainer) {
      // Define padding (buffer zone) for better scrolling experience
      const PADDING = 80; // pixels of padding around the active cell
      
      // Get container and cell dimensions
      const containerRect = scrollContainer.getBoundingClientRect();
      const cellRect = cellElement.getBoundingClientRect();
      
      // Calculate if cell needs scrolling with padding
      const needsScrollUp = cellRect.top - PADDING < containerRect.top;
      const needsScrollDown = cellRect.bottom + PADDING > containerRect.bottom;
      const needsScrollLeft = cellRect.left - PADDING < containerRect.left;
      const needsScrollRight = cellRect.right + PADDING > containerRect.right;
      
      // Calculate scroll positions with padding
      if (needsScrollUp) {
        const scrollTop = scrollContainer.scrollTop - (containerRect.top - cellRect.top) - PADDING;
        scrollContainer.scrollTo({ top: scrollTop, behavior: 'smooth' });
      } else if (needsScrollDown) {
        const scrollTop = scrollContainer.scrollTop + (cellRect.bottom - containerRect.bottom) + PADDING;
        scrollContainer.scrollTo({ top: scrollTop, behavior: 'smooth' });
      }
      
      if (needsScrollLeft) {
        const scrollLeft = scrollContainer.scrollLeft - (containerRect.left - cellRect.left) - PADDING;
        scrollContainer.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      } else if (needsScrollRight) {
        const scrollLeft = scrollContainer.scrollLeft + (cellRect.right - containerRect.right) + PADDING;
        scrollContainer.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  }, []);

  useEffect(() => {
    if (onCellSelect && currentCell.row !== null && currentCell.col !== null) {
      // Only call if the values have meaningfully changed or during specific actions
      const prevSelection = prevSelectionRef.current;
      const hasChanged = 
        prevSelection.row !== currentCell.row || 
        prevSelection.col !== currentCell.col || 
        !isDragging; // Don't call during dragging
      
      if (hasChanged) {
        const selectedCells = getSelectedCells();
        // Make sure to pass ALL parameters, including the current cell's row and column
        onCellSelect(currentCell.row, currentCell.col, selectionRange, selectedCells);
        
        // Scroll cell into view when selection changes
        scrollCellIntoView(currentCell.row, currentCell.col);
        
        // Update ref with current values
        prevSelectionRef.current = {
          row: currentCell.row,
          col: currentCell.col,
          range: selectionRange
        };
      }
    }
  }, [currentCell, selectionRange, onCellSelect, getSelectedCells, isDragging, scrollCellIntoView]);

  const handleMouseDown = (row, col, e) => {
    // Handle multi-select with Ctrl key
    if (e && e.ctrlKey) {
      // Start a new selection range for ctrl+drag
      setMultiSelectRanges(prev => [...prev, { 
        start: { row, col }, 
        end: { row, col } 
      }]);
    } else {
      // Clear multi-select ranges if ctrl is not pressed
      if (multiSelectRanges.length > 0) {
        setMultiSelectRanges([]);
      }
      
      setCurrentCell({ row, col });
      setSelectionRange({
        start: { row, col },
        end: { row, col }
      });
    }
    
    setEditingCell({ row: null, col: null });
    setIsDragging(true);
    
    // Important: Call onCellSelect here to ensure the selection is recognized immediately
    const selectedCells = [{ row, col }];
    onCellSelect?.(row, col, { start: { row, col }, end: { row, col } }, selectedCells);
  };

  const handleMouseEnter = (row, col) => {
    if (isDragging) {
      if (isCtrlPressed && multiSelectRanges.length > 0) {
        // Update the last range in multi-select mode
        setMultiSelectRanges(prev => {
          const newRanges = [...prev];
          const lastRangeIndex = newRanges.length - 1;
          
          newRanges[lastRangeIndex] = {
            ...newRanges[lastRangeIndex],
            end: { row, col }
          };
          
          return newRanges;
        });
      } else {
        setCurrentCell({ row, col });
        setSelectionRange(prev => ({
          ...prev,
          end: { row, col }
        }));
      }
    }
  };

  const startEditingOrReplaceCell = (e) => {
    if (currentCell.row === null || currentCell.col === null) return;
    
    const { row, col } = currentCell;
    
    // Make Enter key start editing but not replace cell content
    if (e.key === 'Enter') {
      // Enter edit mode with current content
      setEditingCell(currentCell);
      return;
    }
    
    // For printable characters, directly replace cell content
    const isPrintableKey = e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey;
    
    if (isPrintableKey && editingCell.row === null) {
      e.preventDefault();
      
      // Save current state for undo
      addToHistory([...data]);
      
      // Create new data with the pressed key as the content
      const newData = [...data];
      newData[row] = [...newData[row]];
      newData[row][col] = e.key;
      
      // Update the table data
      onChange(newData);
      
      // Enter edit mode to continue typing
      setEditingCell({ row, col });
    }
  };

  // Update the handleKeyDown function in TableEditor component

const handleKeyDown = useCallback((e) => {
  if (currentCell.row === null || currentCell.col === null) return;

  // Handle Copy (Ctrl/Cmd + C)
  if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
    e.preventDefault();
    copySelectedCells();
    return;
  }
  
  // Handle Cut (Ctrl/Cmd + X)
  if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
    e.preventDefault();
    cutSelectedCells();
    return;
  }

  // Handle Paste (Ctrl/Cmd + V)
  if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
    e.preventDefault();
    pasteToCurrentCell();
    return;
  }
  
  // Handle Undo (Ctrl/Cmd + Z)
  if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
    e.preventDefault();
    handleUndo();
    return;
  }

   // Handle Ctrl+D for green highlighting
   if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
    e.preventDefault();
    
    // Only proceed if we have a selection
    if (selectionRange.start.row !== null) {
      // Get all selected cells and apply green highlighting
      const selectedCells = getSelectedCells();
      selectedCells.forEach(({ row, col }) => {
        const key = `${row}-${col}`;
        // Only apply green highlight if it doesn't already have one
        // This prevents toggling off existing green highlights
        if (highlights[key] !== '#4caf50') {
          onHighlight(row, col, '#4caf50'); // Green color
        }
      });
    }
    return;
  }

  // Handle Ctrl+Q for red highlighting
  if ((e.ctrlKey || e.metaKey) && e.key === 'q') {
    e.preventDefault();
    
    // Only proceed if we have a selection
    if (selectionRange.start.row !== null) {
      // Get all selected cells and apply red highlighting
      const selectedCells = getSelectedCells();
      selectedCells.forEach(({ row, col }) => {
        const key = `${row}-${col}`;
        // Only apply red highlight if it doesn't already have one
        if (highlights[key] !== '#f44336') {
          onHighlight(row, col, '#f44336'); // Red color
        }
      });
    }
    return;
  }

  // Handle direct cell editing or cell content replacement
  if (!editingCell.row && !editingCell.col) {
    startEditingOrReplaceCell(e);
  }
  
  // Handle Delete/Backspace
  if ((e.key === 'Delete' || e.key === 'Backspace') && editingCell.row === null) {
    e.preventDefault();
    
    // Save current state for undo
    addToHistory([...data]);
    
    const selectedCells = getSelectedCells();
    const newData = [...data];
    
    selectedCells.forEach(({ row, col }) => {
      if (row < newData.length && col < newData[0].length) {
        newData[row] = [...newData[row]];
        newData[row][col] = '';
      }
    });
    
    onChange(newData);
    return;
  }

  // === ENHANCED NAVIGATION LOGIC ===
  
  // Handle Enter key navigation (move down)
  if (e.key === 'Enter') {
    e.preventDefault();
    
    // If we're in edit mode, finish editing and move
    if (editingCell.row !== null && editingCell.col !== null) {
      setEditingCell({ row: null, col: null });
      
      // Check if Shift is pressed to determine direction
      if (e.shiftKey) {
        // Move up with Shift+Enter if possible
        if (currentCell.row > 0) {
          const newRow = currentCell.row - 1;
          setCurrentCell({ row: newRow, col: currentCell.col });
          setSelectionRange({
            start: { row: newRow, col: currentCell.col },
            end: { row: newRow, col: currentCell.col }
          });
          onCellSelect?.(newRow, currentCell.col, 
            { start: { row: newRow, col: currentCell.col }, 
              end: { row: newRow, col: currentCell.col } }, 
            [{ row: newRow, col: currentCell.col }]);
        }
      } else {
        // Move down with regular Enter if possible
        if (currentCell.row < data.length - 1) {
          const newRow = currentCell.row + 1;
          setCurrentCell({ row: newRow, col: currentCell.col });
          setSelectionRange({
            start: { row: newRow, col: currentCell.col },
            end: { row: newRow, col: currentCell.col }
          });
          onCellSelect?.(newRow, currentCell.col, 
            { start: { row: newRow, col: currentCell.col }, 
              end: { row: newRow, col: currentCell.col } }, 
            [{ row: newRow, col: currentCell.col }]);
        }
      }
    } else {
      // If we're not in edit mode, just enter edit mode
      setEditingCell(currentCell);
    }
    return;
  }

  // Handle Tab key navigation (move right)
  if (e.key === 'Tab') {
    e.preventDefault();
    
    // If we're in edit mode, finish editing first
    if (editingCell.row !== null && editingCell.col !== null) {
      setEditingCell({ row: null, col: null });
    }
    
    // Check for Shift+Tab (move left)
    if (e.shiftKey) {
      if (currentCell.col > 0) {
        // Move to previous column
        const newCol = currentCell.col - 1;
        setCurrentCell({ row: currentCell.row, col: newCol });
        setSelectionRange({
          start: { row: currentCell.row, col: newCol },
          end: { row: currentCell.row, col: newCol }
        });
        onCellSelect?.(currentCell.row, newCol, 
          { start: { row: currentCell.row, col: newCol }, 
            end: { row: currentCell.row, col: newCol } }, 
          [{ row: currentCell.row, col: newCol }]);
      } else if (currentCell.row > 0) {
        // Move to end of previous row
        const newRow = currentCell.row - 1;
        const newCol = data[0].length - 1;
        setCurrentCell({ row: newRow, col: newCol });
        setSelectionRange({
          start: { row: newRow, col: newCol },
          end: { row: newRow, col: newCol }
        });
        onCellSelect?.(newRow, newCol, 
          { start: { row: newRow, col: newCol }, 
            end: { row: newRow, col: newCol } }, 
          [{ row: newRow, col: newCol }]);
      }
    } else {
      // Regular Tab (move right)
      if (currentCell.col < data[0].length - 1) {
        // Move to next column
        const newCol = currentCell.col + 1;
        setCurrentCell({ row: currentCell.row, col: newCol });
        setSelectionRange({
          start: { row: currentCell.row, col: newCol },
          end: { row: currentCell.row, col: newCol }
        });
        onCellSelect?.(currentCell.row, newCol, 
          { start: { row: currentCell.row, col: newCol }, 
            end: { row: currentCell.row, col: newCol } }, 
          [{ row: currentCell.row, col: newCol }]);
      } else if (currentCell.row < data.length - 1) {
        // Move to start of next row
        const newRow = currentCell.row + 1;
        setCurrentCell({ row: newRow, col: 0 });
        setSelectionRange({
          start: { row: newRow, col: 0 },
          end: { row: newRow, col: 0 }
        });
        onCellSelect?.(newRow, 0, 
          { start: { row: newRow, col: 0 }, 
            end: { row: newRow, col: 0 } }, 
          [{ row: newRow, col: 0 }]);
      }
    }
    return;
  }

  // If we're not in edit mode, handle other navigation keys
  if (editingCell.row === null) {
    switch (e.key) {
      case 'Escape':
        // Clear multi-selection if any
        if (multiSelectRanges.length > 0) {
          setMultiSelectRanges([]);
        }
        setEditingCell({ row: null, col: null });
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        if (currentCell.row > 0) {
          const newRow = currentCell.row - 1;
          setCurrentCell({ row: newRow, col: currentCell.col });
          if (!e.shiftKey) {
            setSelectionRange({
              start: { row: newRow, col: currentCell.col },
              end: { row: newRow, col: currentCell.col }
            });
          } else {
            setSelectionRange(prev => ({
              ...prev,
              end: { row: newRow, col: currentCell.col }
            }));
          }
          onCellSelect?.(newRow, currentCell.col, 
            e.shiftKey ? selectionRange : {
              start: { row: newRow, col: currentCell.col },
              end: { row: newRow, col: currentCell.col }
            }, getSelectedCells());
        }
        break;
      
      case 'ArrowDown':
        e.preventDefault();
        if (currentCell.row < data.length - 1) {
          const newRow = currentCell.row + 1;
          setCurrentCell({ row: newRow, col: currentCell.col });
          if (!e.shiftKey) {
            setSelectionRange({
              start: { row: newRow, col: currentCell.col },
              end: { row: newRow, col: currentCell.col }
            });
          } else {
            setSelectionRange(prev => ({
              ...prev,
              end: { row: newRow, col: currentCell.col }
            }));
          }
          onCellSelect?.(newRow, currentCell.col, 
            e.shiftKey ? selectionRange : {
              start: { row: newRow, col: currentCell.col },
              end: { row: newRow, col: currentCell.col }
            }, getSelectedCells());
        }
        break;
      
      case 'ArrowLeft':
        e.preventDefault();
        if (currentCell.col > 0) {
          const newCol = currentCell.col - 1;
          setCurrentCell({ row: currentCell.row, col: newCol });
          if (!e.shiftKey) {
            setSelectionRange({
              start: { row: currentCell.row, col: newCol },
              end: { row: currentCell.row, col: newCol }
            });
          } else {
            setSelectionRange(prev => ({
              ...prev,
              end: { row: currentCell.row, col: newCol }
            }));
          }
          onCellSelect?.(currentCell.row, newCol, 
            e.shiftKey ? selectionRange : {
              start: { row: currentCell.row, col: newCol },
              end: { row: currentCell.row, col: newCol }
            }, getSelectedCells());
        }
        break;
      
      case 'ArrowRight':
        e.preventDefault();
        if (currentCell.col < data[0].length - 1) {
          const newCol = currentCell.col + 1;
          setCurrentCell({ row: currentCell.row, col: newCol });
          if (!e.shiftKey) {
            setSelectionRange({
              start: { row: currentCell.row, col: newCol },
              end: { row: currentCell.row, col: newCol }
            });
          } else {
            setSelectionRange(prev => ({
              ...prev,
              end: { row: currentCell.row, col: newCol }
            }));
          }
          onCellSelect?.(currentCell.row, newCol, 
            e.shiftKey ? selectionRange : {
              start: { row: currentCell.row, col: newCol },
              end: { row: currentCell.row, col: newCol }
            }, getSelectedCells());
        }
        break;
      
      default:
        break;
    }
  }
}, [
  currentCell, 
  editingCell, 
  data, 
  onCellSelect, 
  getSelectedCells, 
  copySelectedCells, 
  cutSelectedCells,
  pasteToCurrentCell,
  handleUndo,
  addToHistory,
  selectionRange,
  multiSelectRanges
]);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleCellClick = (row, col, e) => {
    if (e.ctrlKey) {
      // Multi-select with Ctrl key
      setMultiSelectRanges(prev => [...prev, { 
        start: { row, col }, 
        end: { row, col } 
      }]);
    } else {
      // Clear multi-select ranges if ctrl is not pressed
      if (multiSelectRanges.length > 0) {
        setMultiSelectRanges([]);
      }
      
      setCurrentCell({ row, col });
      setSelectionRange({
        start: { row, col },
        end: { row, col }
      });
    }
    
    setEditingCell({ row: null, col: null });
    
    // Pass complete information to parent component
    onCellSelect?.(row, col, 
      { start: { row, col }, end: { row, col } }, 
      [{ row, col }]);
  };

  const handleCellDoubleClick = (row, col) => {
    setEditingCell({ row, col });
  };

  const handleCellChange = (row, col, value) => {
    // Save current state for undo
    addToHistory([...data]);
    
    const newData = data.map((r, rowIndex) =>
      rowIndex === row
        ? r.map((cell, colIndex) =>
            colIndex === col ? value : cell
          )
        : r
    );
    onChange(newData);
  };

  const getSelectionStyles = (row, col) => {
    // Check both primary selection and multi-select ranges
    const inMainRange = isInRange(row, col);
    const inMultiRange = isInMultiSelectRange(row, col);
    
    if (!inMainRange && !inMultiRange) return '';
    
    // Determine selection type and get the relevant range
    const isMulti = !inMainRange && inMultiRange;
    const range = isMulti 
      ? multiSelectRanges.find(range => {
          const { start, end } = range;
          const minRow = Math.min(start.row, end.row);
          const maxRow = Math.max(start.row, end.row);
          const minCol = Math.min(start.col, end.col);
          const maxCol = Math.max(start.col, end.col);
          
          return row >= minRow && row <= maxRow && col >= minCol && col <= maxCol;
        })
      : selectionRange;
      
    if (!range) return '';
    
    const { start, end } = range;
    const minRow = Math.min(start.row, end.row);
    const maxRow = Math.max(start.row, end.row);
    const minCol = Math.min(start.col, end.col);
    const maxCol = Math.max(start.col, end.col);

    // Determine if cell is on an edge
    const isTopEdge = row === minRow;
    const isBottomEdge = row === maxRow;
    const isLeftEdge = col === minCol;
    const isRightEdge = col === maxCol;

    // Base background style - different for multi-select
    const bgStyle = isMulti 
      ? (isDarkMode ? 'bg-purple-900/30' : 'bg-purple-100') 
      : (isDarkMode ? 'bg-blue-900/30' : 'bg-blue-100');

    // Border styles
    const borderStyles = [];
    if (isTopEdge) borderStyles.push(isMulti ? 'border-t-2 border-t-purple-500' : 'border-t-2 border-t-blue-500');
    if (isBottomEdge) borderStyles.push(isMulti ? 'border-b-2 border-b-purple-500' : 'border-b-2 border-b-blue-500');
    if (isLeftEdge) borderStyles.push(isMulti ? 'border-l-2 border-l-purple-500' : 'border-l-2 border-l-blue-500');
    if (isRightEdge) borderStyles.push(isMulti ? 'border-r-2 border-r-purple-500' : 'border-r-2 border-r-blue-500');

    return `${bgStyle} ${borderStyles.join(' ')}`;
  };

  // Modified renderCell function - removed the auto-highlighting logic
  const renderCell = (cell, row, col) => {
    const isEditing = editingCell.row === row && editingCell.col === col;
    const highlightKey = `${row}-${col}`;
    const highlightColor = highlights[highlightKey];
    const selectionStyles = getSelectionStyles(row, col);
  
    return (
      <div 
        key={`${row}-${col}`}
        ref={el => { cellRefs.current[`${row}-${col}`] = el }}
        data-row={row}
        data-col={col}
        className={`w-40 h-8 border-r border-b relative select-none
          ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}
          ${selectionStyles}`}
        onMouseDown={(e) => handleMouseDown(row, col, e)}
        onMouseEnter={() => handleMouseEnter(row, col)}
        onClick={(e) => handleCellClick(row, col, e)}
        onDoubleClick={() => handleCellDoubleClick(row, col)}
        style={{
          backgroundColor: highlightColor ? `${highlightColor}40` : undefined
        }}
      >
        {isEditing ? (
          <input
            type="text"
            value={cell || ''}
            onChange={(e) => handleCellChange(row, col, e.target.value)}
            className={`w-full h-full px-2 focus:outline-none overflow-hidden text-ellipsis whitespace-nowrap
              ${isDarkMode ? 'bg-transparent text-gray-300' : 'bg-transparent'}`}
            autoFocus
          />
        ) : (
          <div className={`w-full h-full px-2 flex items-center overflow-hidden whitespace-nowrap
            ${isDarkMode ? 'text-gray-300' : ''}`}>
            <span className="overflow-hidden text-ellipsis">{cell}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="relative min-w-full" ref={gridRef}>
      {/* Table wrapper with fixed headers */}
      <div className="relative" ref={tableWrapperRef}>
        {/* Fixed top-left corner */}
        <div className={`absolute top-0 left-0 z-20 w-10 h-8 border-r border-b ${
          isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
        } flex items-center justify-center text-sm`} />
        
        {/* Fixed column headers row - sticky top */}
        <div className="sticky top-0 z-10 flex">
          {/* Empty cell to align with row headers */}
          <div className="w-10 h-8" />
          
          {/* Column Headers */}
          {data[0]?.map((_, colIndex) => (
            <div
              key={colIndex}
              onClick={(e) => selectEntireColumn(colIndex, e)}
              className={`w-40 h-8 border-r border-b sticky top-0 ${
                isDarkMode ? 'border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700' 
                          : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'
              }
              ${isInRange(0, colIndex) || isInMultiSelectRange(0, colIndex) ? (isDarkMode ? 'ring-2 ring-blue-500 bg-blue-900/30' 
                : 'ring-2 ring-blue-500 bg-blue-100') : ''}
              flex items-center justify-center text-sm font-medium cursor-pointer select-none`}
            >
              {String.fromCharCode(65 + colIndex)}
            </div>
          ))}
        </div>

        {/* Data Rows with sticky row headers */}
        {data.map((row, rowIndex) => (
          <div key={rowIndex} className="flex">
            {/* Row Header - sticky left */}
            <div
              onClick={(e) => selectEntireRow(rowIndex, e)}
              className={`w-10 h-8 border-r border-b sticky left-0 z-10 ${
                isDarkMode ? 'border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700' 
                          : 'border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100'}
                ${isInRange(rowIndex, 0) || isInMultiSelectRange(rowIndex, 0) ? 
                  (isDarkMode ? 'ring-2 ring-blue-500 bg-blue-900/30' : 'ring-2 ring-blue-500 bg-blue-100') : ''} 
                flex items-center justify-center text-sm font-medium cursor-pointer select-none`}
            >
              {rowIndex + 1}
            </div>

            {/* Row Cells */}
            {row.map((cell, colIndex) => renderCell(cell, rowIndex, colIndex))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TableEditor;